import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';

const TechnicalStack = () => {
  const [activeCategory, setActiveCategory] = useState('core');

  // Animate tech cards on scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, { threshold: 0.1 });

    const cards = document.querySelectorAll('.tech-card');
    cards.forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
      card.style.transition = 'all 0.6s ease-out';
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, [activeCategory]); // Re-run when category changes

  const showCategory = (categoryId) => {
    setActiveCategory(categoryId);
  };

  const copyCode = () => {
    const codeText = document.querySelector('.code-content pre').textContent;
    navigator.clipboard.writeText(codeText).then(() => {
      const button = document.querySelector('.copy-button');
      const originalText = button.textContent;
      button.textContent = 'Copied!';
      button.style.background = 'rgba(103, 232, 169, 0.2)';
      button.style.color = '#67e8a9';
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = '';
        button.style.color = '';
      }, 2000);
    });
  };

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Container */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 100px 2rem 2rem;
        }
        
        /* Header */
        .page-header {
            text-align: center;
            margin-bottom: 4rem;
        }
        
        .page-title {
            font-size: 3.5rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.3rem;
            color: #888;
        }
        
        /* Category Tabs */
        .category-tabs {
            display: flex;
            justify-content: center;
            gap: 1rem;
            margin-bottom: 3rem;
            flex-wrap: wrap;
        }
        
        .tab-button {
            padding: 0.8rem 2rem;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            color: #888;
            cursor: pointer;
            transition: all 0.3s;
            font-size: 1rem;
            font-weight: 600;
        }
        
        .tab-button:hover {
            background: rgba(255, 255, 255, 0.1);
            color: white;
        }
        
        .tab-button.active {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-color: transparent;
        }
        
        /* Stack Categories */
        .stack-category {
            display: none;
            animation: fadeIn 0.5s ease;
        }
        
        .stack-category.active {
            display: block;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        /* Technology Cards */
        .tech-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
            gap: 2rem;
            margin-bottom: 3rem;
        }
        
        .tech-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            transition: all 0.3s;
            position: relative;
            overflow: hidden;
        }
        
        .tech-card::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -50%;
            width: 100%;
            height: 100%;
            background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
            transition: all 0.5s;
            opacity: 0;
        }
        
        .tech-card:hover::before {
            opacity: 1;
            top: -20%;
            right: -20%;
        }
        
        .tech-card:hover {
            transform: translateY(-5px);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .tech-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1.5rem;
        }
        
        .tech-icon {
            width: 50px;
            height: 50px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
        }
        
        .tech-info {
            flex: 1;
        }
        
        .tech-name {
            font-size: 1.3rem;
            font-weight: bold;
            margin-bottom: 0.2rem;
        }
        
        .tech-version {
            font-size: 0.9rem;
            color: #667eea;
        }
        
        .tech-description {
            color: #bbb;
            margin-bottom: 1rem;
            line-height: 1.5;
        }
        
        .tech-features {
            list-style: none;
        }
        
        .tech-features li {
            padding: 0.4rem 0;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: #999;
            font-size: 0.9rem;
        }
        
        .tech-features li::before {
            content: '▸';
            color: #667eea;
        }
        
        .tech-links {
            display: flex;
            gap: 1rem;
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .tech-link {
            color: #667eea;
            text-decoration: none;
            font-size: 0.9rem;
            transition: color 0.3s;
        }
        
        .tech-link:hover {
            color: #764ba2;
        }
        
        /* Architecture Diagram */
        .architecture-overview {
            background: rgba(255, 255, 255, 0.02);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            padding: 3rem;
            margin: 4rem 0;
        }
        
        .architecture-layers {
            display: flex;
            flex-direction: column;
            gap: 2rem;
        }
        
        .layer {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 1.5rem;
            position: relative;
        }
        
        .layer-name {
            font-size: 1.2rem;
            font-weight: bold;
            color: #667eea;
            margin-bottom: 1rem;
        }
        
        .layer-techs {
            display: flex;
            flex-wrap: wrap;
            gap: 1rem;
        }
        
        .mini-tech {
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
            padding: 0.5rem 1rem;
            border-radius: 8px;
            font-size: 0.9rem;
            transition: all 0.3s;
            cursor: pointer;
        }
        
        .mini-tech:hover {
            background: rgba(102, 126, 234, 0.2);
            transform: scale(1.1) rotate(2deg);
        }
        
        /* Dependencies Section */
        .dependencies-section {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin: 3rem 0;
        }
        
        .dependency-list {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 1rem;
            margin-top: 1.5rem;
        }
        
        .dependency-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.8rem;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 8px;
            transition: all 0.3s;
        }
        
        .dependency-item:hover {
            background: rgba(255, 255, 255, 0.08);
        }
        
        .dependency-name {
            font-weight: 600;
        }
        
        .dependency-version {
            color: #667eea;
            font-family: monospace;
            font-size: 0.9rem;
        }
        
        /* Code Example */
        .code-section {
            margin: 3rem 0;
        }
        
        .code-container {
            background: #1a1a1a;
            border: 1px solid #333;
            border-radius: 12px;
            overflow: hidden;
        }
        
        .code-header {
            background: #2a2a2a;
            padding: 1rem 1.5rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid #333;
        }
        
        .code-title {
            font-weight: 600;
            color: #888;
        }
        
        .copy-button {
            background: rgba(102, 126, 234, 0.2);
            border: 1px solid rgba(102, 126, 234, 0.3);
            color: #667eea;
            padding: 0.4rem 1rem;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.3s;
            font-size: 0.9rem;
        }
        
        .copy-button:hover {
            background: rgba(102, 126, 234, 0.3);
        }
        
        .code-content {
            padding: 1.5rem;
            overflow-x: auto;
        }
        
        .code-content pre {
            margin: 0;
            color: #e0e0e0;
            font-family: 'Consolas', 'Monaco', monospace;
            font-size: 0.9rem;
            line-height: 1.6;
        }
        
        /* Performance Metrics */
        .performance-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1.5rem;
            margin: 2rem 0;
        }
        
        .perf-metric {
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
            border-radius: 12px;
            padding: 1.5rem;
            text-align: center;
        }
        
        .perf-value {
            font-size: 2rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .perf-label {
            color: #888;
            font-size: 0.9rem;
            margin-top: 0.5rem;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
            .tech-grid {
                grid-template-columns: 1fr;
            }
            
            .category-tabs {
                flex-direction: column;
                align-items: stretch;
            }
            
            .tab-button {
                width: 100%;
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Stack Technique</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Stack Technique Complète</h1>
          <p className="page-subtitle">Technologies et outils powering l'Agent RAG Hybride</p>
        </div>
        
        {/* Category Tabs */}
        <div className="category-tabs">
          <button 
            className={`tab-button ${activeCategory === 'core' ? 'active' : ''}`} 
            onClick={() => showCategory('core')}
          >
            Core Technologies
          </button>
          <button 
            className={`tab-button ${activeCategory === 'search' ? 'active' : ''}`} 
            onClick={() => showCategory('search')}
          >
            Search & Indexing
          </button>
          <button 
            className={`tab-button ${activeCategory === 'ai' ? 'active' : ''}`} 
            onClick={() => showCategory('ai')}
          >
            AI & Models
          </button>
          <button 
            className={`tab-button ${activeCategory === 'infra' ? 'active' : ''}`} 
            onClick={() => showCategory('infra')}
          >
            Infrastructure
          </button>
          <button 
            className={`tab-button ${activeCategory === 'tools' ? 'active' : ''}`} 
            onClick={() => showCategory('tools')}
          >
            Tools & Monitoring
          </button>
        </div>
        
        {/* Core Technologies */}
        <div className={`stack-category ${activeCategory === 'core' ? 'active' : ''}`}>
          <h2 style={{ marginBottom: '2rem' }}>Technologies Principales</h2>
          <div className="tech-grid">
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">🔗</div>
                <div className="tech-info">
                  <div className="tech-name">LangChain</div>
                  <div className="tech-version">v0.1.0</div>
                </div>
              </div>
              <div className="tech-description">
                Framework d'orchestration pour applications LLM. Gère le workflow RAG, le chaining et l'intégration des différents composants.
              </div>
              <ul className="tech-features">
                <li>Chain composition flexible</li>
                <li>Memory management intégré</li>
                <li>Support multi-modèles</li>
                <li>Retrieval pipeline optimisé</li>
              </ul>
              <div className="tech-links">
                <a href="#" className="tech-link">Documentation</a>
                <a href="#" className="tech-link">GitHub</a>
              </div>
            </div>
            
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">⚡</div>
                <div className="tech-info">
                  <div className="tech-name">FastAPI</div>
                  <div className="tech-version">v0.104</div>
                </div>
              </div>
              <div className="tech-description">
                Framework web moderne et rapide pour construire l'API REST. Support natif async/await et validation automatique.
              </div>
              <ul className="tech-features">
                <li>Performance async native</li>
                <li>Documentation auto (Swagger)</li>
                <li>Type hints Python</li>
                <li>WebSocket support</li>
              </ul>
              <div className="tech-links">
                <a href="#" className="tech-link">Documentation</a>
                <a href="#" className="tech-link">Examples</a>
              </div>
            </div>
            
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">💾</div>
                <div className="tech-info">
                  <div className="tech-name">Redis</div>
                  <div className="tech-version">v7.2</div>
                </div>
              </div>
              <div className="tech-description">
                Cache haute performance pour stocker embeddings, résultats de recherche et sessions. Support des structures de données avancées.
              </div>
              <ul className="tech-features">
                <li>Cache multi-niveaux</li>
                <li>Pub/Sub pour real-time</li>
                <li>Persistence optionnelle</li>
                <li>Cluster mode ready</li>
              </ul>
              <div className="tech-links">
                <a href="#" className="tech-link">Documentation</a>
                <a href="#" className="tech-link">Best Practices</a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Search & Indexing */}
        <div className={`stack-category ${activeCategory === 'search' ? 'active' : ''}`}>
          <h2 style={{ marginBottom: '2rem' }}>Moteurs de Recherche & Indexation</h2>
          <div className="tech-grid">
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">🔍</div>
                <div className="tech-info">
                  <div className="tech-name">FAISS</div>
                  <div className="tech-version">v1.7.4</div>
                </div>
              </div>
              <div className="tech-description">
                Librairie Facebook pour recherche vectorielle efficace. Optimisée pour les grandes collections d'embeddings avec support GPU.
              </div>
              <ul className="tech-features">
                <li>Index IVF pour scalabilité</li>
                <li>Support GPU (optionnel)</li>
                <li>Quantization pour mémoire</li>
                <li>Batch processing</li>
              </ul>
            </div>
            
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">🎯</div>
                <div className="tech-info">
                  <div className="tech-name">Elasticsearch</div>
                  <div className="tech-version">v8.11</div>
                </div>
              </div>
              <div className="tech-description">
                Moteur de recherche full-text avec support BM25 natif. Utilisé pour la recherche par mots-clés et filtrage avancé.
              </div>
              <ul className="tech-features">
                <li>BM25 ranking optimisé</li>
                <li>Analyzers multilingues</li>
                <li>Aggregations puissantes</li>
                <li>Near real-time indexing</li>
              </ul>
            </div>
            
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">🕸️</div>
                <div className="tech-info">
                  <div className="tech-name">Neo4j</div>
                  <div className="tech-version">v5.15</div>
                </div>
              </div>
              <div className="tech-description">
                Base de données graphe pour modéliser les relations complexes entre documents, concepts et réglementations.
              </div>
              <ul className="tech-features">
                <li>Cypher query language</li>
                <li>ACID transactions</li>
                <li>Graph algorithms built-in</li>
                <li>Visual exploration</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* AI & Models */}
        <div className={`stack-category ${activeCategory === 'ai' ? 'active' : ''}`}>
          <h2 style={{ marginBottom: '2rem' }}>Intelligence Artificielle & Modèles</h2>
          <div className="tech-grid">
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">🤖</div>
                <div className="tech-info">
                  <div className="tech-name">GPT-4 Turbo</div>
                  <div className="tech-version">Latest</div>
                </div>
              </div>
              <div className="tech-description">
                Modèle principal pour génération de réponses. Context window de 128k tokens pour traiter des documents longs.
              </div>
              <ul className="tech-features">
                <li>128k context window</li>
                <li>Function calling</li>
                <li>JSON mode</li>
                <li>Multi-language support</li>
              </ul>
            </div>
            
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">🧠</div>
                <div className="tech-info">
                  <div className="tech-name">Sentence-BERT</div>
                  <div className="tech-version">all-MiniLM-L6-v2</div>
                </div>
              </div>
              <div className="tech-description">
                Modèle d'embeddings optimisé pour la similarité sémantique. Excellent ratio performance/qualité.
              </div>
              <ul className="tech-features">
                <li>384 dimensions</li>
                <li>Multilingue (50+ langues)</li>
                <li>Fast inference</li>
                <li>Fine-tunable</li>
              </ul>
            </div>
            
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">🎯</div>
                <div className="tech-info">
                  <div className="tech-name">Cross-Encoder</div>
                  <div className="tech-version">ms-marco-MiniLM</div>
                </div>
              </div>
              <div className="tech-description">
                Modèle de re-ranking pour affiner les résultats. Score précis de pertinence entre query et documents.
              </div>
              <ul className="tech-features">
                <li>Re-ranking précis</li>
                <li>Pairwise scoring</li>
                <li>Optimized for relevance</li>
                <li>Low latency</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Infrastructure */}
        <div className={`stack-category ${activeCategory === 'infra' ? 'active' : ''}`}>
          <h2 style={{ marginBottom: '2rem' }}>Infrastructure & Déploiement</h2>
          <div className="tech-grid">
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">🐳</div>
                <div className="tech-info">
                  <div className="tech-name">Docker</div>
                  <div className="tech-version">v24.0</div>
                </div>
              </div>
              <div className="tech-description">
                Containerisation de tous les composants pour déploiement reproductible et isolation.
              </div>
              <ul className="tech-features">
                <li>Multi-stage builds</li>
                <li>Docker Compose orchestration</li>
                <li>Layer caching</li>
                <li>Security scanning</li>
              </ul>
            </div>
            
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">☸️</div>
                <div className="tech-info">
                  <div className="tech-name">Kubernetes</div>
                  <div className="tech-version">v1.28</div>
                </div>
              </div>
              <div className="tech-description">
                Orchestration pour production avec auto-scaling, self-healing et gestion avancée des ressources.
              </div>
              <ul className="tech-features">
                <li>HPA for auto-scaling</li>
                <li>ConfigMaps & Secrets</li>
                <li>Ingress management</li>
                <li>StatefulSets for DBs</li>
              </ul>
            </div>
            
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">⚖️</div>
                <div className="tech-info">
                  <div className="tech-name">HAProxy</div>
                  <div className="tech-version">v2.8</div>
                </div>
              </div>
              <div className="tech-description">
                Load balancer haute performance pour distribuer le trafic et assurer la haute disponibilité.
              </div>
              <ul className="tech-features">
                <li>Layer 7 routing</li>
                <li>Health checks</li>
                <li>SSL termination</li>
                <li>Rate limiting</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Tools & Monitoring */}
        <div className={`stack-category ${activeCategory === 'tools' ? 'active' : ''}`}>
          <h2 style={{ marginBottom: '2rem' }}>Outils & Monitoring</h2>
          <div className="tech-grid">
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">📊</div>
                <div className="tech-info">
                  <div className="tech-name">Prometheus</div>
                  <div className="tech-version">v2.48</div>
                </div>
              </div>
              <div className="tech-description">
                Système de monitoring et alerting pour collecter métriques et déclencher des alertes.
              </div>
              <ul className="tech-features">
                <li>Time-series database</li>
                <li>PromQL queries</li>
                <li>Service discovery</li>
                <li>Alert manager</li>
              </ul>
            </div>
            
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">📈</div>
                <div className="tech-info">
                  <div className="tech-name">Grafana</div>
                  <div className="tech-version">v10.2</div>
                </div>
              </div>
              <div className="tech-description">
                Plateforme de visualisation pour créer des dashboards interactifs et suivre les KPIs en temps réel.
              </div>
              <ul className="tech-features">
                <li>Custom dashboards</li>
                <li>Real-time updates</li>
                <li>Alerting integration</li>
                <li>Multi-datasource</li>
              </ul>
            </div>
            
            <div className="tech-card">
              <div className="tech-header">
                <div className="tech-icon">🔍</div>
                <div className="tech-info">
                  <div className="tech-name">OpenTelemetry</div>
                  <div className="tech-version">v1.21</div>
                </div>
              </div>
              <div className="tech-description">
                Framework d'observabilité pour traces distribuées, métriques et logs unifiés.
              </div>
              <ul className="tech-features">
                <li>Distributed tracing</li>
                <li>Metrics collection</li>
                <li>Log correlation</li>
                <li>Vendor agnostic</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Architecture Overview */}
        <div className="architecture-overview">
          <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Vue d'Ensemble de l'Architecture</h2>
          <div className="architecture-layers">
            <div className="layer">
              <div className="layer-name">Frontend Layer</div>
              <div className="layer-techs">
                <span className="mini-tech">API Gateway</span>
                <span className="mini-tech">Load Balancer</span>
                <span className="mini-tech">WebSocket</span>
                <span className="mini-tech">JWT Auth</span>
              </div>
            </div>
            
            <div className="layer">
              <div className="layer-name">Application Layer</div>
              <div className="layer-techs">
                <span className="mini-tech">FastAPI</span>
                <span className="mini-tech">LangChain</span>
                <span className="mini-tech">Query Router</span>
                <span className="mini-tech">Result Fusion</span>
              </div>
            </div>
            
            <div className="layer">
              <div className="layer-name">Search Layer</div>
              <div className="layer-techs">
                <span className="mini-tech">FAISS</span>
                <span className="mini-tech">Elasticsearch</span>
                <span className="mini-tech">Neo4j</span>
                <span className="mini-tech">MongoDB</span>
              </div>
            </div>
            
            <div className="layer">
              <div className="layer-name">Data Layer</div>
              <div className="layer-techs">
                <span className="mini-tech">Redis Cache</span>
                <span className="mini-tech">S3 Storage</span>
                <span className="mini-tech">PostgreSQL</span>
                <span className="mini-tech">Backup</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Dependencies */}
        <div className="dependencies-section">
          <h2>Dependencies Python Principales</h2>
          <div className="dependency-list">
            <div className="dependency-item">
              <span className="dependency-name">langchain</span>
              <span className="dependency-version">0.1.0</span>
            </div>
            <div className="dependency-item">
              <span className="dependency-name">openai</span>
              <span className="dependency-version">1.6.1</span>
            </div>
            <div className="dependency-item">
              <span className="dependency-name">sentence-transformers</span>
              <span className="dependency-version">2.2.2</span>
            </div>
            <div className="dependency-item">
              <span className="dependency-name">faiss-cpu</span>
              <span className="dependency-version">1.7.4</span>
            </div>
            <div className="dependency-item">
              <span className="dependency-name">fastapi</span>
              <span className="dependency-version">0.104.0</span>
            </div>
            <div className="dependency-item">
              <span className="dependency-name">redis</span>
              <span className="dependency-version">5.0.1</span>
            </div>
            <div className="dependency-item">
              <span className="dependency-name">elasticsearch</span>
              <span className="dependency-version">8.11.0</span>
            </div>
            <div className="dependency-item">
              <span className="dependency-name">neo4j</span>
              <span className="dependency-version">5.15.0</span>
            </div>
          </div>
        </div>
        
        {/* Code Example */}
        <div className="code-section">
          <h2 style={{ marginBottom: '1.5rem' }}>Configuration Docker Compose</h2>
          <div className="code-container">
            <div className="code-header">
              <span className="code-title">docker-compose.yml</span>
              <button className="copy-button" onClick={copyCode}>Copy</button>
            </div>
            <div className="code-content">
              <pre>{`version: '3.8'

services:
  api:
    build: .
    ports:
      - "5000:5000"
    environment:
      - REDIS_HOST=redis
      - ES_HOST=elasticsearch
      - NEO4J_URI=bolt://neo4j:7687
    depends_on:
      - redis
      - elasticsearch
      - neo4j

  redis:
    image: redis:7.2-alpine
    ports:
      - "6379:6379"
    volumes:
      - redis_data:/data

  elasticsearch:
    image: elasticsearch:8.11.0
    environment:
      - discovery.type=single-node
      - xpack.security.enabled=false
    ports:
      - "9200:9200"
    volumes:
      - es_data:/usr/share/elasticsearch/data

  neo4j:
    image: neo4j:5.15
    ports:
      - "7474:7474"
      - "7687:7687"
    environment:
      - NEO4J_AUTH=neo4j/password
    volumes:
      - neo4j_data:/data

volumes:
  redis_data:
  es_data:
  neo4j_data:`}</pre>
            </div>
          </div>
        </div>
        
        {/* Performance Metrics */}
        <div style={{ margin: '4rem 0' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Métriques de Performance</h2>
          <div className="performance-grid">
            <div className="perf-metric">
              <div className="perf-value">50ms</div>
              <div className="perf-label">Cache Response</div>
            </div>
            <div className="perf-metric">
              <div className="perf-value">800ms</div>
              <div className="perf-label">Search Latency</div>
            </div>
            <div className="perf-metric">
              <div className="perf-value">200ms</div>
              <div className="perf-label">Fusion Time</div>
            </div>
            <div className="perf-metric">
              <div className="perf-value">650ms</div>
              <div className="perf-label">LLM Generation</div>
            </div>
            <div className="perf-metric">
              <div className="perf-value">99.9%</div>
              <div className="perf-label">Uptime SLA</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TechnicalStack;